import { NavLink } from 'react-router-dom';
import { Search } from 'lucide-react';
import { cn } from '@reflex/ui/lib/utils';
import { searchFieldShellClasses } from '@/components/shared/search-field-shell';
import { SectionLabel } from '@reflex/ui/components/layout/Typography';
import { navRowClasses } from '../NavBar/nav-utils';
import type { NavItem } from '../NavBar/nav-types';

export interface TakeoverNavGroup {
  /** Group heading; an empty label renders the items with no heading (pinned rows). */
  label: string;
  items: NavItem[];
}

/**
 * The search field takeover asides share, sized onto the nav rail.
 */
export function TakeoverSearchField({
  query,
  setQuery,
  placeholder,
}: {
  query: string;
  setQuery: (q: string) => void;
  placeholder: string;
}) {
  return (
    <div className="px-1.5 pb-1">
      {/* pl-[11px]: the field's 1px border eats into the 12px inset that
          lands the glyph on the shared 18px icon rail. */}
      <div className={cn(searchFieldShellClasses, 'flex h-8 items-center gap-2.5 pl-[11px] pr-2')}>
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          aria-label={placeholder}
          className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground/70"
        />
      </div>
    </div>
  );
}

/**
 * Grouped nav rows every takeover aside shares: section label per group,
 * rows through `navRowClasses` with the deepest match highlighted by the
 * caller. Test ids are `<testIdPrefix>-<to>`.
 */
export function TakeoverNavGroups({
  groups,
  activeItemPath,
  testIdPrefix,
}: {
  groups: TakeoverNavGroup[];
  activeItemPath: string | null;
  testIdPrefix: string;
}) {
  return (
    <>
      {groups.map((group, index) => (
        <div key={group.label || `pinned-${index}`} className="flex flex-col gap-0.5">
          {group.label !== '' && (
            <SectionLabel as="span" className="block px-3 pb-1 pt-3">
              {group.label}
            </SectionLabel>
          )}
          {group.items.map(({ to, label, icon: Icon, tutorialId }) => {
            const active = to === activeItemPath;
            return (
              <NavLink
                key={to}
                to={to}
                data-testid={`${testIdPrefix}-${to}`}
                data-tutorial-id={tutorialId}
                data-active={active ? 'true' : undefined}
                className={navRowClasses(active)}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="min-w-0 flex-1 truncate">{label}</span>
              </NavLink>
            );
          })}
        </div>
      ))}
      {groups.length === 0 && (
        <SectionLabel as="span" className="block px-3 pt-3">
          No matches
        </SectionLabel>
      )}
    </>
  );
}
